import './index.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ChatHistoryProvider } from './hooks/useChatHistory';
import { Layout } from './components/Layout/Layout';
import { LoginPage } from './pages/Login';
import { DashboardPage } from './pages/Dashboard';
import { NodesPage } from './pages/Nodes';
import { ProjectsPage } from './pages/Projects';
import { TestPage } from './pages/Test';
import { ChatPage } from './pages/Chat';
import { AdminUsersPage } from './pages/AdminUsers';
import { AdminPlansPage } from './pages/AdminPlans';
import { Deployments as DeploymentsPage } from './pages/Deployments';

// --- SSO & Pipeline Parameter Extraction ---
// We must extract these before React Router mounts, because <Navigate to="/chat" replace />
// on the root route will immediately strip the hash and search query before components can read them!
(function extractSsoParams() {
  const hash = window.location.hash;
  const search = window.location.search;
  
  let token: string | null = null;
  let workspace: string | null = null;
  let agentId: string | null = null;
  let tunnelUrl: string | null = null;
  let hostname: string | null = null;
  let prompt: string | null = null;
  let pipeline: string | null = null;

  const extractFromParams = (params: URLSearchParams) => {
    if (!token) token = params.get('token') || params.get('agent_token') || params.get('sso_token');
    if (!workspace) workspace = params.get('workspace') || params.get('project') || params.get('projectName') || params.get('target_project') || params.get('path');
    if (!agentId) agentId = params.get('agent_id');
    if (!tunnelUrl) tunnelUrl = params.get('tunnel_url');
    if (!hostname) hostname = params.get('hostname');
    if (!prompt) prompt = params.get('prompt') || params.get('instruction') || params.get('message') || params.get('query') || params.get('task');
    if (!pipeline) pipeline = params.get('pipeline') || params.get('pipeline_name') || params.get('action');
  };

  if (hash) {
    extractFromParams(new URLSearchParams(hash.replace('#', '')));
  }

  if (search) {
    extractFromParams(new URLSearchParams(search));
  }

  if (token) {
    localStorage.setItem('eric_mec_local_token', token);
  }

  if (workspace) {
    const existingStr = localStorage.getItem('eric_mec_workspaces');
    let workspaces: string[] = [];
    try {
      if (existingStr) workspaces = JSON.parse(existingStr);
    } catch (e) {
      workspaces = [];
    }
    if (Array.isArray(workspaces) && !workspaces.includes(workspace)) {
      workspaces.push(workspace);
      localStorage.setItem('eric_mec_workspaces', JSON.stringify(workspaces));
      localStorage.setItem('eric_mec_workspaces_dirty', Date.now().toString());
    }
    localStorage.setItem('ericoder_active_project', workspace);

    // Save auto new chat trigger for the pushed project / pipeline
    const autoPrompt = prompt || (pipeline ? `Thực thi pipeline: ${pipeline}` : '');
    const autoChatPayload = {
      project: workspace,
      prompt: autoPrompt,
      pipeline: pipeline || null,
      timestamp: Date.now()
    };
    sessionStorage.setItem('ericoder_auto_new_chat', JSON.stringify(autoChatPayload));
  }

  if (agentId || tunnelUrl) {
    const activeAgent = {
      agent_id: agentId || 'local-agent',
      hostname: hostname || 'Local PC',
      tunnel_url: tunnelUrl || 'http://localhost:8080',
      agent_token: token || '',
      workspaces: workspace ? [workspace] : []
    };
    localStorage.setItem('eric_mec_active_agent', JSON.stringify(activeAgent));
  }

  if (token || workspace || agentId || tunnelUrl || prompt || pipeline) {
    // Clean URL hash and search query so parameters aren't visible or logged
    window.history.replaceState(null, '', window.location.pathname);
  }
})();

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isLoggedIn, loading } = useAuth();
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: '16px' }}>
      <div className="spinner" style={{ width: 32, height: 32 }} />
      <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Loading Ericoder...</span>
    </div>
  );
  return isLoggedIn ? <>{children}</> : <Navigate to="/login" replace />;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (!user) return null; // or loading
  return user.role === 'admin' ? <>{children}</> : <Navigate to="/chat" replace />;
}

function App() {
  return (
    <AuthProvider>
      <ChatHistoryProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={
              <PrivateRoute>
                <Layout />
              </PrivateRoute>
            }>
              <Route index element={<Navigate to="/chat" replace />} />
              <Route path="chat" element={<ChatPage />} />
              
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="api-keys" element={<Navigate to="/dashboard" replace />} />
              <Route path="deployments" element={<DeploymentsPage />} />

              {/* Admin Only Routes */}
              <Route path="nodes" element={<AdminRoute><NodesPage /></AdminRoute>} />
              <Route path="projects" element={<AdminRoute><ProjectsPage /></AdminRoute>} />
              <Route path="test" element={<AdminRoute><TestPage /></AdminRoute>} />
              <Route path="admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
              <Route path="admin/plans" element={<AdminRoute><AdminPlansPage /></AdminRoute>} />
            </Route>
          </Routes>
        </BrowserRouter>
      </ChatHistoryProvider>
    </AuthProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode><App /></StrictMode>
);
